import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPublicFormsList, PublicFormListItem } from "../api/publicFormsApi";

const DESCRIPTION_MAX_LENGTH = 400;
const RICH_TEXT_CLASS = "[&_p]:mb-1 [&_p:last-child]:mb-0 [&_a]:text-blue-600 [&_a]:underline";

const trimDescription = (html: string): string => {
  if (!html || html.length <= DESCRIPTION_MAX_LENGTH) return html;
  const stripped = html.replace(/<[^>]+>/g, "");
  if (stripped.length <= DESCRIPTION_MAX_LENGTH) return html;
  let length = 0;
  let result = "";
  const segmenter = /<[^>]+>|[^<]+/g;
  let m: RegExpExecArray | null;
  while ((m = segmenter.exec(html)) !== null && length < DESCRIPTION_MAX_LENGTH) {
    if (m[0].startsWith("<")) {
      result += m[0];
    } else {
      const take = m[0].slice(0, DESCRIPTION_MAX_LENGTH - length);
      result += take;
      length += take.length;
      if (take.length < m[0].length) {
        result += "…";
        break;
      }
    }
    if (!m[0].startsWith("<")) length += m[0].length;
  }
  return result;
};

const PublicFormsLanding = () => {
  const [forms, setForms] = useState<PublicFormListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchPublicFormsList();
        setForms(data);
      } catch (err: any) {
        setError(err.message || "Failed to load forms");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full min-h-screen">
        <div className="panel w-[320px] p-6 shadow-md">
          <h1 className="text-center mb-2">Loading forms...</h1>
          <p className="text-sm text-center text-gray-500">Please wait.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center w-full min-h-screen">
        <div className="panel w-[320px] p-6 shadow-md">
          <h1 className="text-center mb-2">Forms unavailable</h1>
          <p className="text-sm text-center text-red-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center w-full min-h-screen py-10">
      <div className="panel w-full max-w-[720px] p-6 shadow-md space-y-4">
        <div>
          <h1 className="text-2xl font-semibold">Public forms</h1>
          <p className="text-sm text-gray-500">Select a form to start.</p>
        </div>
        {forms.length === 0 && (
          <p className="text-sm text-gray-500">No published forms available.</p>
        )}
        <div className="space-y-3">
          {forms.map((form) => (
            <Link
              key={form.uid}
              to={`/forms/${form.publicToken}`}
              className="block border rounded-md p-4 hover:border-gray-300 hover:bg-gray-50 transition-colors"
            >
              <h2 className="text-lg font-medium">{form.title}</h2>
              {form.description && (
                <div
                  className={`text-sm text-gray-500 mt-1 line-clamp-3 ${RICH_TEXT_CLASS}`}
                  dangerouslySetInnerHTML={{
                    __html: trimDescription(form.description),
                  }}
                />
              )}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PublicFormsLanding;
